import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Trophy, Clock, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Chronograph format: seconds -> M:SS.mmm
const chrono = (s) => {
  if (s == null) return "-";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  return `${m}:${String(sec).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
};

const MEDAL = { 1: "#FFD24A", 2: "#C8C8D0", 3: "#D8894A" };
const AD_DIMS = { single: [324, 77], double: [324, 132], triple: [324, 209] };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// 1 -> "1st Place", 2 -> "2nd Place", 3 -> "3rd Place", 4 -> "4th Place", ...
const placeLabel = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]} Place`;
};
// trim trailing zeros: 6.000 -> "6", 6.500 -> "6.5"
const trimNum = (n) => `${+Number(n).toFixed(3)}`;

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/contests/${id}/results`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="text-race-sub py-20 text-center" data-testid="results-error">Results are not available for this contest.</div>;
  if (!data) return <div className="text-race-sub py-20 text-center">Loading final results…</div>;

  const rows = data.results || [];
  const [adW, adH] = AD_DIMS[data.post_race_ad_format] || AD_DIMS.single;
  const adSrc = data.post_race_ad_url ? `${BACKEND}${data.post_race_ad_url}` : null;

  return (
    <div className="max-w-2xl mx-auto animate-slide-up" data-testid="results-page">
      <button onClick={() => navigate("/calendar")} className="flex items-center gap-2 text-race-sub hover:text-race-txt text-sm mb-4" data-testid="results-back">
        <ArrowLeft size={15} /> Calendar
      </button>
      <div className="flex items-center gap-2 text-race-red mb-2"><Trophy size={18} /><span className="overline">Official Final Results · Sanctioned</span></div>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">{data.name}</h1>
      <p className="text-race-sub mt-1 capitalize">{data.race_type} · this contest has been raced and is now closed · {data.total_participants} finisher(s)</p>

      {adSrc && (
        <div className="mt-5" data-testid="results-postrace-ad">
          {data.sponsor_name && <div className="overline text-race-muted mb-1">Sponsored by {data.sponsor_name}</div>}
          <div className="mx-auto bg-race-surface border border-race-line rounded-sm overflow-hidden"
               style={{ aspectRatio: `${adW} / ${adH}`, maxWidth: adW }}>
            <img src={adSrc} alt="Finish-line sponsor ad" className="w-full h-full object-contain" data-testid="results-postrace-ad-img" />
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="results-board">
        {rows.length === 0 && <p className="text-race-sub text-sm" data-testid="results-empty">No finishers were recorded for this contest.</p>}
        {(() => {
          const top = rows.filter((r) => !r.dqt && r.place).slice(0, 3);
          if (top.length < 2) return null;
          const order = top.length >= 3 ? [top[1], top[0], top[2]] : [top[1], top[0]];
          const podH = { 1: "h-24", 2: "h-16", 3: "h-12" };
          return (
            <div className="flex items-end justify-center gap-4 mb-6 pb-4 border-b border-race-line" data-testid="results-podium">
              {order.map((r) => (
                <div key={r.user_id || r.place} className="flex flex-col items-center" style={{ width: 96 }}>
                  <div className={`rounded-full overflow-hidden mb-1 border-2 bg-race-red text-race-bg flex items-center justify-center font-heading ${r.place === 1 ? "w-16 h-16" : "w-12 h-12"}`}
                       style={{ borderColor: MEDAL[r.place] || "#9A9AA2" }}>
                    {r.avatar ? <img src={r.avatar.startsWith("http") ? r.avatar : `${BACKEND}${r.avatar}`} alt="" className="w-full h-full object-cover" /> : (r.name || "R").slice(0, 1).toUpperCase()}
                  </div>
                  <span className="text-xs font-medium truncate max-w-full text-center">{r.user_id === user?.id ? "You" : r.name}</span>
                  <span className="text-[10px] text-race-sub tabular">{chrono(r.final_time)}</span>
                  <div className={`w-full ${podH[r.place]} mt-1 rounded-t-sm flex items-start justify-center pt-1 font-heading text-lg`}
                       style={{ background: `${MEDAL[r.place] || "#9A9AA2"}22`, color: MEDAL[r.place] || "#9A9AA2" }}>{placeLabel(r.place)}</div>
                </div>
              ))}
            </div>
          );
        })()}
        {rows.map((r) => {
          const mine = r.user_id && r.user_id === user?.id;
          const penalties = r.errors || 0;
          const penaltySecs = r.penalty_time || 0;
          const isDqt = !!r.dqt;
          const rowKey = r.user_id || (isDqt ? `dqt-${r.name}` : r.place);
          return (
            <div key={rowKey} data-testid={isDqt ? `result-row-dqt-${rowKey}` : `result-row-${r.place}`}
              className={`bg-race-surface border rounded-md px-4 py-3 ${mine ? "border-race-volt" : isDqt ? "border-race-red/40" : "border-race-line"}`}>
              <div className="flex items-center gap-3">
                <span className="font-heading text-lg sm:text-xl leading-none shrink-0"
                  style={{ color: isDqt ? "#FF2A00" : (MEDAL[r.place] || "#9A9AA2") }}
                  data-testid={isDqt ? `result-place-dqt-${rowKey}` : `result-place-${r.place}`}>
                  {isDqt ? "DQT" : placeLabel(r.place)}
                </span>
                <span className="font-medium flex-1 truncate flex items-center gap-2 text-left">
                  <span className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-race-red text-race-bg flex items-center justify-center font-heading text-sm" data-testid={`result-avatar-${rowKey}`}>
                    {r.avatar ? <img src={r.avatar.startsWith("http") ? r.avatar : `${BACKEND}${r.avatar}`} alt="" className="w-full h-full object-cover" /> : (r.name || "R").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="truncate">{r.name}{mine ? " (you)" : ""}</span>
                </span>
                <span className="tabular text-lg flex items-center gap-1"><Clock size={14} className="text-race-sub" /> {chrono(r.final_time)}</span>
              </div>
              {isDqt && (
                <div className="mt-1 text-[11px] text-race-red font-medium" data-testid={`result-dqt-note-${rowKey}`}>
                  Disqualified Time - {penalties}+ mistakes. Characters credited; time not eligible for a prize.
                </div>
              )}
              {/* Receipt: raw time + penalties = final time, with true accuracy */}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-race-sub tabular" data-testid={isDqt ? `result-breakdown-dqt-${rowKey}` : `result-breakdown-${r.place}`}>
                <span>Raw {chrono(r.raw_time)}</span>
                {penalties > 0 ? (
                  <span className="text-race-red">
                    −{trimNum(penaltySecs)}s ({penalties} {penalties === 1 ? "Penalty" : "Penalties"} × {data.penalty_seconds}s)
                  </span>
                ) : (
                  <span className="text-race-volt">No penalties</span>
                )}
                <span className="text-race-txt">= Final {chrono(r.final_time)}</span>
                <span>· {r.cps} CPS</span>
                <span>· {r.accuracy != null ? `${r.accuracy}%` : "-"} accuracy</span>
                {r.flagged && <span className="text-race-red">· under review</span>}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-race-muted text-xs mt-6">Top 10 shown. The Top 100 finishers are retained for audit. Times are recorded on the IBRA chronograph; penalties are added to raw time for the final result.</p>
    </div>
  );
}
